/**
 * A short follow-up after an answer: "when", "what time is that?", "where tho".
 * It stays on the last topic. It does not repeat the steps, and it does not
 * become the title of a task already open.
 */
export type FollowUpAspect = "when" | "event" | "where" | "who" | "repeat" | "recurs";

const WHEN = new Set([
  "when",
  "whens it on",
  "when's it on",
  "what time is it at",
  "what date was that",
  "sorry what date was that",
  "the date",
  "when is it",
  "when is that",
  "when is that event",
  "when is it happening",
  "what time",
  "what time is that",
  "what time is it then",
  "what day",
  "what day is that",
  "the time",
  "the date",
  "the day",
  "wen",
]);
const EVENT = new Set([
  "what event",
  "what event tho",
  "what event is that",
  "which event",
  "which event is that",
  "which one",
  "which one tho",
  "which one is it",
  "what one",
  "that one",
  "the event",
  "which",
  "whats it called",
  "what's it called",
  "the name",
]);
// "where is that?" is the stuck phrase ("I don't see it"), not "where is the event?"
const WHERE = new Set([
  "where",
  "where is it",
  "where is it happening",
  "where is that happening",
  "where tho",
  "where do they need to be",
  "whats the venue",
  "what's the venue",
  "location",
  "wheres it",
  "wher",
]);
const WHO = new Set(["who's it going to", "whos it going to", "who did that go to", "who can see it", "which parents get it", "is it for everyone or just one class", "who", "who is it for", "who is that for", "who is it for then", "whos it for", "who's it for"]);
/** Whether the last Event repeats: "are they recurring?", "does it repeat?" ("repeat" below is "say it again"). */
const RECURS = new Set([
  "are they recurring",
  "are those recurring",
  "is it recurring",
  "is that recurring",
  "is it a recurring event",
  "does it repeat",
  "does that repeat",
  "do they repeat",
  "does it recur",
  "do they recur",
  "is it weekly",
  "is it every week",
  "does it happen every week",
]);
const REPEAT = new Set([
  "huh",
  "more",
  "details",
  "tell me more",
  "go on",
  "say again",
  "again",
  "more please",
  "so",
  "and",
  "what",
  "can you say that again",
  "say that more simply",
  "i don't get it",
  "i dont get it",
  "i'm confused",
  "im confused",
  "too long",
  "shorter",
  "shorter please",
  "what does that mean",
  "is that right",
  "are you sure",
  "that didn't help",
  "that didnt help",
  "this doesn't help",
  "this doesnt help",
  "its more than that",
  "its more than that isnt it",
  "it's more than that",
  "it's more than that isn't it",
  "no like do you know",
]);

/** Lead-in words that don't change the follow-up: "k when", "um when", "and where". */
const FILLER = /^(?:(?:um+|uh+|ok(?:ay)?|so|and|k|right|well|hmm+|sure|yes|yeah|but)\s+)+/;

function normalised(text: string): string {
  return text
    .toLowerCase()
    .replace(/[?!.,]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(FILLER, "")
    .trim();
}

/** Which word they are asking, or null when this is a new question. */
export function followUpAspect(text: string): FollowUpAspect | null {
  const said = normalised(text);
  if (WHEN.has(said)) return "when";
  if (EVENT.has(said)) return "event";
  if (WHERE.has(said)) return "where";
  if (WHO.has(said)) return "who";
  if (RECURS.has(said)) return "recurs";
  if (REPEAT.has(said)) return "repeat";
  return null;
}

/** How many words a fragment can have and still be a follow-up rather than a new question. */
const SHORT_FOLLOW_UP_WORDS = 6;

/** A fragment short enough to score against the follow-up phrases. A full question is longer. */
export function isShortFollowUp(text: string): boolean {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length <= SHORT_FOLLOW_UP_WORDS;
}

/**
 * Phrases the matcher scores a short follow-up against. The tiny exact phrases
 * ("so", "and", "which") stay in the sets above and are not scored, so a new
 * question is not pulled in by one shared word.
 */
export const FUZZY_ASPECT_PHRASES: { aspect: FollowUpAspect; phrase: string }[] = [
  { aspect: "when", phrase: "when is that" },
  { aspect: "when", phrase: "what time is that" },
  { aspect: "when", phrase: "what day is that" },
  { aspect: "event", phrase: "which event is that" },
  { aspect: "event", phrase: "which one is that" },
  { aspect: "event", phrase: "what event is that" },
  { aspect: "where", phrase: "where is it happening" },
  { aspect: "where", phrase: "where is that happening" },
  { aspect: "who", phrase: "who is that for" },
  { aspect: "who", phrase: "who is it for" },
  { aspect: "repeat", phrase: "can you say that again" },
  { aspect: "repeat", phrase: "tell me more" },
  { aspect: "repeat", phrase: "say that more simply" },
];

/**
 * The follow-up those phrase scores point at, when one aspect is clearly ahead.
 * `scores` lines up with `FUZZY_ASPECT_PHRASES`.
 */
export function aspectFromScores(scores: number[], answer: number, clearLead: number): FollowUpAspect | null {
  if (scores.length !== FUZZY_ASPECT_PHRASES.length) return null;
  const best = new Map<FollowUpAspect, number>();
  scores.forEach((score, i) => {
    const aspect = FUZZY_ASPECT_PHRASES[i].aspect;
    best.set(aspect, Math.max(score, best.get(aspect) ?? Number.NEGATIVE_INFINITY));
  });
  const ranked = Array.from(best.entries()).sort((a, b) => b[1] - a[1]);
  const top = ranked[0];
  const next = ranked[1];
  if (!top || top[1] < answer) return null;
  if (next && top[1] - next[1] < clearLead) return null;
  return top[0];
}

/**
 * A day, a clock time, or an explicit "no time". "when you press Publish" does
 * not count: that word is not a day or a time.
 */
const HAS_WHEN =
  /\b(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday|january|february|march|april|june|july|august|september|october|november|december|today|tomorrow|tonight)\b|\b\d{1,2}:\d{2}\b|\b(?:am|pm)\b|\b(?:\d+\s+)?(?:minutes?|hours?)\b|\b(?:dates?|times?|days?)\b|\bno\s+(?:clock\s+)?(?:day|time)\b|\bnot a time\b|\bclock time\b/i;

export const mentionsWhen = (text: string): boolean => HAS_WHEN.test(text);

/** A real Event named in the reply, not the word "Event" in a set of steps. */
const WEEKDAY = "monday|tuesday|wednesday|thursday|friday|saturday|sunday";

export const namesAnEvent = (text: string): boolean =>
  /[“"][^“”"]{2,}[”"]/.test(text) ||
  /coming up:/.test(text) ||
  new RegExp(`\\bis on\\s+(?:${WEEKDAY})\\b`, "i").test(text) ||
  new RegExp(`\\bthat's\\s+(?:${WEEKDAY})\\b`, "i").test(text) ||
  new RegExp(`^(?:${WEEKDAY}),`, "i").test(text);

const ASKING_WHICH_EVENT = new Set([
  "calendar.createEvent",
  "calendar.addEventHowTo",
  "helpCenter.createEvent",
  "create.eventRepeatHowTo",
  "create.eventReminderHowTo",
  "create.eventAudienceHowTo",
  "create.cancelEventHowTo",
  "create.eventNotifyHowTo",
  "create.rsvpHowTo",
  "create.staffEventHowTo",
  "troubleshooting.eventNotOnCalendar",
]);

/** What to say for "which event?" when the last answer did not name one. */
export function eventLine(id: string): string {
  if (id === "unsupported.zoom") return "There is no class call. If you mean an Event, name it.";
  if (id === "introduction") {
    return "That was about what I can help with. It is not about one Event. Name the Event if you want one looked up.";
  }
  if (ASKING_WHICH_EVENT.has(id) || id.startsWith("create.event") || id.startsWith("calendar.")) {
    return "Which Event do you mean? I don't have a title until you choose one, and I haven't saved an Event.";
  }
  return "It is not about one Event. Name the Event if you want one looked up.";
}

const NO_TIME: Record<string, string> = {
  "calendar.createEvent":
    "You set the day and time on Create an event, or with ADD EVENT. I don't have one until you choose it, and I haven't created the Event.",
  "calendar.addEventHowTo":
    "You set the day and time on Create an event, or with ADD EVENT. I don't have one until you choose it, and I haven't created the Event.",
  "helpCenter.createEvent":
    "You set the day and time on Create an event, or with ADD EVENT. I don't have one until you choose it, and I haven't created the Event.",
  "create.eventRepeatHowTo":
    "You pick the day, such as Friday, while you create the Event and set it to repeat. I haven't saved a series.",
  "create.eventReminderHowTo":
    "You set the reminder while you create the Event, or press Send reminder on a sent Notice. I haven't sent one, and there is no clock time until you choose it.",
  "notices.reminderHowTo":
    "You set the reminder while you create the Event, or press Send reminder on a sent Notice. I haven't sent one, and there is no clock time until you choose it.",
  "notices.sendReminder":
    "You set the reminder while you create the Event, or press Send reminder on a sent Notice. I haven't sent one, and there is no clock time until you choose it.",
  "create.cancelEventHowTo":
    "Send the cancellation when you are ready, with a Notice or the Cancelled / weather template. I haven't cancelled it, and there is no clock time until you do.",
  "create.eventNotifyHowTo":
    "Parents are told in the app when you press Publish. There is no separate clock time for that notification.",
  "create.staffEventHowTo":
    "Choose Internal team only while you review the Event, before Publish. That choice has no clock time.",
  "home.userCount": "All Users is a count, not a time.",
  "users.total": "All Users is a count, not a time.",
  "account.plan": "The plan has no day or time.",
  "concepts.activeAdverts": "Active Adverts is a count, not a time.",
  "notices.scheduled": "Scheduled is a list of Notices waiting to go out, not a clock time. Nothing is waiting until you set a send time.",
  "concepts.tenSecondNotice": "Home says Send a notice in 10s. That is how quick the shortcut is, not a time of day.",
  "create.pollVoteHowTo": "They vote in the app after the poll is sent. There is no clock time for that.",
  "create.claimTakenHowTo": "The option disappears as soon as they claim it. There is no clock time.",
  "create.claimListHowTo": "A claim list has no clock time. The option disappears when a parent claims it.",
  "unsupported.zoom": "There is no call, so there is no time. An Event can carry a time and a place once you create one.",
  "calendar.monthHowTo": "Calendar opens on this month, and TODAY jumps to today. A time belongs to an Event, not to the page.",
  "home.noticeTypes": "The chart is a count, not a time.",
  "create.sendToOneParent": "A message to one parent has no clock time until you set one. Nothing goes out until you press Publish.",
  "create.scheduleOffer": "You set the date and time on the Notice before Publish. There is no clock time until you do.",
  "concepts.whereIsAsk": "Ask Noticeboard is the button in the top bar, next to the profile picture. It has no time.",
  "concepts.doesNotSend": "Nothing goes out until you press Publish. There is no clock time until you set one.",
  "concepts.homeNumbers": "Those Home cards are counts, not a time.",
  "concepts.gettingStarted": "Getting Started has no clock time.",
  introduction:
    "What I can help with has no day or time. I can find pages you can open, answer questions about the Organization, and prepare a Notice for you to review.",
};

/** What to say for "when?" when the last answer has no day and no clock time. */
export function noTimeLine(id: string): string {
  const specific = NO_TIME[id];
  if (specific) return specific;
  if (id.startsWith("drive.") || id.startsWith("troubleshooting.upload")) return "Drive has no day or time.";
  if (
    id.startsWith("circles.") ||
    id.startsWith("concepts.circle") ||
    id.startsWith("concepts.sub") ||
    id.startsWith("concepts.whoSees") ||
    id.startsWith("concepts.addTo") ||
    id.startsWith("concepts.createCircle")
  ) {
    return "A Circle has no day or time.";
  }
  if (
    id.startsWith("users.") ||
    id.startsWith("team.") ||
    id === "concepts.inviteParent" ||
    id === "concepts.addTeacher" ||
    id === "concepts.myRole" ||
    id === "concepts.creatorVsOwner" ||
    id === "concepts.addUsersTile"
  ) {
    return "That has no day or time.";
  }
  if (id.startsWith("account.")) return "Account has no day or time.";
  if (id.startsWith("helpCenter.") || id === "concepts.fundamentals" || id === "concepts.circleVideo") {
    return "Help Center has no day or time.";
  }
  if (id.startsWith("certificates.") || id === "concepts.certificates" || id === "concepts.signature") {
    return "A certificate has no day or time unless you put one in the Notice. You still review it before Publish.";
  }
  if (id.startsWith("unsupported.")) return "That isn't something with a time, and it can't be done from here.";
  if (id.startsWith("create.poll") || id === "create.claimVsPoll" || id.startsWith("notices.poll")) {
    return "A poll has no clock time. Parents vote in the app after it is published.";
  }
  if (id.startsWith("notices.")) return "A Notice has no day or time unless it is an Event.";
  if (id.startsWith("create.")) return "Create has no clock time until you set a date on an Event or a scheduled Notice.";
  if (id.startsWith("calendar.")) return "The day and time are chosen while creating the Event. I haven't saved one.";
  if (id.startsWith("home.")) return "The Home cards are counts, not a time.";
  if (id.startsWith("adCenter.") || id === "concepts.adCenter") return "Ad Center has no day or time.";
  return "This has no day or time.";
}
